import { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import {
  RefreshCw, Phone, MessageSquare, Eye, Clock, MapPin,
  CheckCircle, Navigation, User, Calendar, ChevronDown, ChevronUp,
  List, Map as MapIcon
} from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import { getTodayString } from './dateUtils';
import CustomerCommunications from './CustomerCommunications';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different statuses
const createStatusIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${color};
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  "><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

export default function DispatchBoard({ companyId, onNavigate }) {
  const { colors } = useTheme();
  const { showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [techs, setTechs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedTechId, setExpandedTechId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  // Communication modal state
  const [showCommsModal, setShowCommsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadDispatchData();
      // Auto-refresh every 30 seconds
      const interval = setInterval(loadDispatchData, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadDispatchData = async (retryCount = 0) => {
    try {
      setLoading(true);
      const today = getTodayString();

      // Get all techs (users with roles that can have jobs assigned)
      const { data: userCompaniesData, error: techsError } = await supabaseData
        .from('user_companies')
        .select('id, user_id, role')
        .eq('company_id', companyId)
        .in('role', ['field_tech', 'member', 'admin', 'owner']);

      if (techsError) {
        if (retryCount < 2) {
          console.warn('DispatchBoard: query error, retrying...', techsError.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 500));
          return loadDispatchData(retryCount + 1);
        }
        throw techsError;
      }

      // Get user profiles for all tech user_ids
      const userIds = (userCompaniesData || []).map(uc => uc.user_id);
      let profilesMap = {};
      let locationsMap = {};

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabaseData
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', userIds);

        if (!profilesError && profilesData) {
          profilesData.forEach(profile => {
            profilesMap[profile.id] = profile;
          });
        }

        // Fetch tech locations from tech_locations table (last 24 hours)
        const { data: locationData } = await supabaseData
          .from('tech_locations')
          .select('user_id, latitude, longitude, accuracy, event_type, updated_at')
          .eq('company_id', companyId)
          .in('user_id', userIds)
          .gte('updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

        if (locationData) {
          locationData.forEach(loc => {
            locationsMap[loc.user_id] = loc;
          });
        }
      }

      // Combine user_companies with profiles and locations
      const techsData = (userCompaniesData || []).map(uc => ({
        ...uc,
        user_profiles: profilesMap[uc.user_id]
          ? { ...profilesMap[uc.user_id], ...(locationsMap[uc.user_id] || {}) }
          : null
      }));

      // Get today's jobs with customer data
      const { data: jobsData, error: jobsError } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            id,
            name,
            first_name,
            last_name,
            phone,
            address,
            city,
            state,
            zip
          )
        `)
        .eq('company_id', companyId)
        .eq('scheduled_date', today)
        .order('scheduled_time');

      if (jobsError) {
        if (retryCount < 2) {
          console.warn('DispatchBoard: jobs query error, retrying...', jobsError.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 500));
          return loadDispatchData(retryCount + 1);
        }
        throw jobsError;
      }

      setTechs(techsData || []);
      setJobs(jobsData || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dispatch data:', error);
      // Only show error if we have no data at all
      if (techs.length === 0 && jobs.length === 0) {
        showError('Error loading dispatch data: ' + (error.message || 'Network error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Get tech's display name
  const getTechName = (tech) => {
    if (tech.user_profiles?.full_name) {
      // Show first name + last initial
      const parts = tech.user_profiles.full_name.split(' ');
      if (parts.length > 1) {
        return `${parts[0]} ${parts[parts.length - 1][0]}.`;
      }
      return parts[0];
    }
    return tech.user_profiles?.email?.split('@')[0] || 'Unknown';
  };

  // Get jobs assigned to a tech
  const getTechJobs = (techUserId) => {
    return jobs.filter(job => job.assigned_tech === techUserId);
  };

  // Get tech's current job (in-progress)
  const getCurrentJob = (techUserId) => {
    const techJobs = getTechJobs(techUserId);
    return techJobs.find(job => job.status === 'in-progress');
  };

  // Get tech's next scheduled job
  const getNextJob = (techUserId) => {
    const techJobs = getTechJobs(techUserId);
    const now = new Date();
    const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    return techJobs
      .filter(job => job.status === 'scheduled' && job.scheduled_time >= currentTimeStr)
      .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''))[0];
  };

  // Calculate tech status
  const getTechStatus = (techUserId) => {
    const techJobs = getTechJobs(techUserId);
    const currentJob = getCurrentJob(techUserId);

    if (currentJob) {
      return { status: 'on_site', label: 'On Site', color: colors.warning };
    }

    const now = new Date();
    const thirtyMinAgo = new Date(now.getTime() - 30 * 60 * 1000);
    const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const thirtyMinAgoStr = `${String(thirtyMinAgo.getHours()).padStart(2, '0')}:${String(thirtyMinAgo.getMinutes()).padStart(2, '0')}`;

    // Check if there's a job scheduled within the past 30 minutes (en route)
    const enRouteJob = techJobs.find(job =>
      job.status === 'scheduled' &&
      job.scheduled_time >= thirtyMinAgoStr &&
      job.scheduled_time <= currentTimeStr
    );

    if (enRouteJob) {
      return { status: 'en_route', label: 'En Route', color: colors.info };
    }

    // Has upcoming jobs today
    const upcomingJobs = techJobs.filter(job =>
      job.status === 'scheduled' && job.scheduled_time > currentTimeStr
    );

    if (upcomingJobs.length > 0) {
      return { status: 'available', label: 'Available', color: colors.success };
    }

    // No jobs scheduled today
    return { status: 'off', label: 'Off', color: colors.textMuted };
  };

  // Calculate time on job
  const getTimeOnJob = (job) => {
    if (job.status !== 'in-progress') return null;

    const startTime = job.started_at ||
      `${job.scheduled_date}T${job.scheduled_time || '00:00'}`;

    const start = new Date(startTime);
    const now = new Date();
    const minutes = Math.floor((now - start) / 60000);

    if (minutes < 0) return null;
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Get customer display name
  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown';
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    return customer.name || 'Unknown';
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'p' : 'a';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes}${ampm}`;
  };

  // Get completed/remaining job counts
  const getJobStats = (techUserId) => {
    const techJobs = getTechJobs(techUserId);
    const completed = techJobs.filter(j => j.status === 'completed').length;
    const remaining = techJobs.filter(j => j.status === 'scheduled' || j.status === 'in-progress').length;
    return { completed, remaining };
  };

  // Handle quick actions
  const handleCallCustomer = (customer) => {
    if (customer?.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
  };

  const handleMessageCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCommsModal(true);
  };

  const handleViewJob = (job) => {
    onNavigate('work-orders', { jobId: job.id });
  };

  // Status indicator dot
  const StatusDot = ({ status }) => {
    const statusInfo = {
      on_site: { color: colors.warning, fill: true },
      en_route: { color: colors.info, fill: false },
      available: { color: colors.success, fill: false },
      off: { color: colors.textMuted, fill: false }
    };
    const info = statusInfo[status] || statusInfo.off;

    return (
      <div style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: info.fill ? info.color : 'transparent',
        border: `2px solid ${info.color}`,
        flexShrink: 0
      }} />
    );
  };

  // Tech Card Component
  const TechCard = ({ tech }) => {
    const techStatus = getTechStatus(tech.user_id);
    const currentJob = getCurrentJob(tech.user_id);
    const nextJob = getNextJob(tech.user_id);
    const stats = getJobStats(tech.user_id);
    const isExpanded = expandedTechId === tech.user_id;
    const timeOnJob = currentJob ? getTimeOnJob(currentJob) : null;

    return (
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        borderColor: isExpanded ? colors.accent : colors.border
      }}>
        {/* Header - Tech Name & Status */}
        <div
          onClick={() => setExpandedTechId(isExpanded ? null : tech.user_id)}
          style={{
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: colors.bgInput,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${techStatus.color}`
            }}>
              <User size={20} color={techStatus.color} />
            </div>
            <div>
              <div style={{ fontWeight: 'bold', color: colors.text, fontSize: '1.1rem' }}>
                {getTechName(tech)}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: techStatus.color,
                fontSize: '0.85rem',
                fontWeight: '500'
              }}>
                <StatusDot status={techStatus.status} />
                {techStatus.label}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              textAlign: 'right',
              marginRight: '0.5rem'
            }}>
              <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>Today</div>
              <div style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '600' }}>
                {stats.completed}/{stats.completed + stats.remaining}
              </div>
            </div>
            {isExpanded ? (
              <ChevronUp size={20} color={colors.textMuted} />
            ) : (
              <ChevronDown size={20} color={colors.textMuted} />
            )}
          </div>
        </div>

        {/* Current Job Section */}
        <div style={{
          padding: '1rem',
          borderBottom: `1px solid ${colors.border}`,
          background: currentJob ? `${colors.warning}10` : 'transparent'
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem'
          }}>
            {currentJob ? 'Current Job' : 'No Active Job'}
          </div>

          {currentJob ? (
            <>
              <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                {getCustomerName(currentJob.customers)}
              </div>
              <div style={{
                fontSize: '0.85rem',
                color: colors.textMuted,
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}>
                <MapPin size={14} />
                {currentJob.customers?.address || 'No address'}
                {currentJob.customers?.city && `, ${currentJob.customers.city}`}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                {timeOnJob && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.25rem 0.5rem',
                    background: colors.warning,
                    color: '#000',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    <Clock size={14} />
                    {timeOnJob}
                  </div>
                )}
                <div style={{
                  fontSize: '0.8rem',
                  color: colors.textSecondary,
                  padding: '0.25rem 0.5rem',
                  background: colors.bgInput,
                  borderRadius: '4px'
                }}>
                  {currentJob.job_type || 'Service'}
                </div>
              </div>
            </>
          ) : nextJob ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: colors.textSecondary,
              fontSize: '0.9rem'
            }}>
              <Calendar size={16} />
              Next: {formatTime(nextJob.scheduled_time)} - {getCustomerName(nextJob.customers)}
            </div>
          ) : (
            <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
              No jobs scheduled today
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{
          padding: '0.75rem 1rem',
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          borderBottom: `1px solid ${colors.border}`
        }}>
          {currentJob?.customers?.phone ? (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleCallCustomer(currentJob.customers); }}
                title="Call Customer"
                style={{
                  padding: '0.5rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.success,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  minHeight: '44px'
                }}
              >
                <Phone size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleMessageCustomer(currentJob.customers); }}
                title="Message Customer"
                style={{
                  padding: '0.5rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.info,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  minHeight: '44px'
                }}
              >
                <MessageSquare size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleViewJob(currentJob); }}
                title="View Job"
                style={{
                  padding: '0.5rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.accent,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  minHeight: '44px'
                }}
              >
                <Eye size={20} />
              </button>
            </>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); nextJob && handleViewJob(nextJob); }}
              title="View Next Job"
              disabled={!nextJob}
              style={{
                padding: '0.5rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: nextJob ? colors.accent : colors.textMuted,
                cursor: nextJob ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '44px',
                minHeight: '44px',
                opacity: nextJob ? 1 : 0.5
              }}
            >
              <Eye size={20} />
            </button>
          )}
        </div>

        {/* Stats Row */}
        <div style={{
          padding: '0.75rem 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          background: colors.bgInput
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            color: colors.success,
            fontSize: '0.85rem'
          }}>
            <CheckCircle size={16} />
            {stats.completed} done
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            color: colors.info,
            fontSize: '0.85rem'
          }}>
            <Calendar size={16} />
            {stats.remaining} left
          </div>
        </div>

        {/* Expanded: Full Job List */}
        {isExpanded && (
          <div style={{
            padding: '1rem',
            borderTop: `1px solid ${colors.border}`,
            background: colors.bg
          }}>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.75rem'
            }}>
              Today's Schedule
            </div>
            {getTechJobs(tech.user_id).length === 0 ? (
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                No jobs scheduled
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {getTechJobs(tech.user_id)
                  .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''))
                  .map(job => (
                    <div
                      key={job.id}
                      onClick={() => handleViewJob(job)}
                      style={{
                        padding: '0.75rem',
                        background: colors.bgCard,
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: job.status === 'completed' ? colors.success :
                                   job.status === 'in-progress' ? colors.warning :
                                   colors.textMuted,
                        flexShrink: 0
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.25rem'
                        }}>
                          <span style={{
                            fontWeight: '600',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}>
                            {formatTime(job.scheduled_time)}
                          </span>
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            background: job.status === 'completed' ? `${colors.success}20` :
                                        job.status === 'in-progress' ? `${colors.warning}20` :
                                        colors.bgInput,
                            color: job.status === 'completed' ? colors.success :
                                   job.status === 'in-progress' ? colors.warning :
                                   colors.textMuted,
                            textTransform: 'capitalize'
                          }}>
                            {job.status}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: colors.textSecondary
                        }}>
                          {getCustomerName(job.customers)} - {job.job_type || 'Service'}
                        </div>
                      </div>
                      <Navigation size={16} color={colors.textMuted} />
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading && techs.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading dispatch board...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.75rem' : '2rem',
          margin: 0,
          color: colors.text
        }}>
          Dispatch Board
        </h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          {/* View Mode Toggle */}
          <div style={{
            display: 'flex',
            background: colors.bgCard,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '0.5rem 0.75rem',
                background: viewMode === 'list' ? colors.accent : 'transparent',
                border: 'none',
                color: viewMode === 'list' ? '#fff' : colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.85rem'
              }}
            >
              <List size={16} />
              {!isMobile && 'List'}
            </button>
            <button
              onClick={() => setViewMode('map')}
              style={{
                padding: '0.5rem 0.75rem',
                background: viewMode === 'map' ? colors.accent : 'transparent',
                border: 'none',
                borderLeft: `1px solid ${colors.border}`,
                color: viewMode === 'map' ? '#fff' : colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.85rem'
              }}
            >
              <MapIcon size={16} />
              {!isMobile && 'Map'}
            </button>
          </div>
          <button
            onClick={loadDispatchData}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            <RefreshCw
              size={16}
              style={{
                animation: loading ? 'spin 1s linear infinite' : 'none'
              }}
            />
            {!isMobile && 'Refresh'}
          </button>
          {lastUpdated && !isMobile && (
            <div style={{
              fontSize: '0.85rem',
              color: colors.textMuted
            }}>
              Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          padding: '1rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.warning }}>
            {techs.filter(t => getTechStatus(t.user_id).status === 'on_site').length}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>On Site</div>
        </div>
        <div style={{
          padding: '1rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.info }}>
            {techs.filter(t => getTechStatus(t.user_id).status === 'en_route').length}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>En Route</div>
        </div>
        <div style={{
          padding: '1rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.success }}>
            {techs.filter(t => getTechStatus(t.user_id).status === 'available').length}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>Available</div>
        </div>
        <div style={{
          padding: '1rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.accent }}>
            {jobs.filter(j => j.status === 'completed').length}/{jobs.length}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>Jobs Done</div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          marginBottom: '1.5rem',
          height: isMobile ? '400px' : '500px'
        }}>
          {(() => {
            // Get techs with location data
            const techsWithLocation = techs.filter(t =>
              t.user_profiles?.latitude && t.user_profiles?.longitude
            );

            if (techsWithLocation.length === 0) {
              return (
                <div style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.textMuted,
                  padding: '2rem'
                }}>
                  <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <h3 style={{ margin: '0 0 0.5rem 0', color: colors.text }}>No Location Data</h3>
                  <p style={{ margin: 0, textAlign: 'center', maxWidth: '300px' }}>
                    Tech locations will appear here when they use the mobile app.
                  </p>
                </div>
              );
            }

            // Calculate center from all tech locations
            const avgLat = techsWithLocation.reduce((sum, t) => sum + t.user_profiles.latitude, 0) / techsWithLocation.length;
            const avgLng = techsWithLocation.reduce((sum, t) => sum + t.user_profiles.longitude, 0) / techsWithLocation.length;

            return (
              <MapContainer
                center={[avgLat, avgLng]}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {techsWithLocation.map(tech => {
                  const techStatus = getTechStatus(tech.user_id);
                  const currentJob = getCurrentJob(tech.user_id);
                  const stats = getJobStats(tech.user_id);
                  const locationTime = tech.user_profiles.updated_at || tech.user_profiles.location_updated_at;
                  const locationAge = locationTime
                    ? Math.floor((Date.now() - new Date(locationTime).getTime()) / 60000)
                    : null;
                  const eventLabel = { app_open: 'App opened', job_start: 'Job started', job_complete: 'Job completed' };

                  return (
                    <Marker
                      key={tech.id}
                      position={[tech.user_profiles.latitude, tech.user_profiles.longitude]}
                      icon={createStatusIcon(techStatus.color)}
                    >
                      <Popup>
                        <div style={{ minWidth: '180px' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                            {getTechName(tech)}
                          </div>
                          <div style={{
                            display: 'inline-block',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            marginBottom: '0.5rem',
                            background: `${techStatus.color}20`,
                            color: techStatus.color
                          }}>
                            {techStatus.label}
                          </div>
                          {currentJob && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                              <strong>Current:</strong> {getCustomerName(currentJob.customers)}
                              <br />
                              <span style={{ color: '#666' }}>{currentJob.job_type || 'Service'}</span>
                            </div>
                          )}
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                            {stats.completed}/{stats.completed + stats.remaining} jobs today
                          </div>
                          {tech.user_profiles.event_type && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#999' }}>
                              Last: {eventLabel[tech.user_profiles.event_type] || tech.user_profiles.event_type}
                            </div>
                          )}
                          {locationAge !== null && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#999' }}>
                              {locationAge < 1 ? 'Just now' :
                                        locationAge < 60 ? `${locationAge}m ago` :
                                        `${Math.floor(locationAge / 60)}h ago`}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            );
          })()}
        </div>
      )}

      {/* Tech Grid */}
      {viewMode === 'list' && (
        techs.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: colors.bgCard,
            borderRadius: '12px',
            border: `2px dashed ${colors.border}`
          }}>
            <User size={48} color={colors.textMuted} style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: colors.text, margin: '0 0 0.5rem 0' }}>No Team Members</h3>
            <p style={{ color: colors.textMuted, margin: 0 }}>
              Add team members to see them on the dispatch board.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '1rem'
          }}>
            {techs
              .sort((a, b) => {
                // Sort by status priority: on_site > en_route > available > off
                const statusOrder = { on_site: 0, en_route: 1, available: 2, off: 3 };
                const aStatus = getTechStatus(a.user_id).status;
                const bStatus = getTechStatus(b.user_id).status;
                return statusOrder[aStatus] - statusOrder[bStatus];
              })
              .map(tech => (
                <TechCard key={tech.id} tech={tech} />
              ))}
          </div>
        )
      )}

      {/* CSS for spin animation and Leaflet fixes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .custom-marker {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
        }
        .leaflet-popup-content {
          margin: 12px;
        }
      `}</style>

      {/* Communications Modal */}
      {showCommsModal && selectedCustomer && (
        <CustomerCommunications
          customer={selectedCustomer}
          companyId={companyId}
          onClose={() => {
            setShowCommsModal(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
}
